import React, { useState, useEffect } from 'react';
import IconPicker from './IconPicker';

const GamesAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const authHeaders = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };

  // Games Management States
  const [games, setGames] = useState([]);
  const [selectedGameForAdmin, setSelectedGameForAdmin] = useState(null);
  const [gameQuestions, setGameQuestions] = useState([]);
  const [gameLevels, setGameLevels] = useState([]);
  const [selectedLevelFilter, setSelectedLevelFilter] = useState(null);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedScriptTab, setSelectedScriptTab] = useState('arabic');
  const [iconPickerGame, setIconPickerGame] = useState(null);
  const [availableIcons, setAvailableIcons] = useState([]);
  const [newQuestion, setNewQuestion] = useState({
    level: 1,
    english_hint: '',
    darija_complete: '',
    missing_word: '',
    alternative_answers: [],
    use_arabic_script: false,
    is_active: true
  });

  // Load Word Challenge DB games on mount
  const loadData = async () => {
    try {
      const gamesRes = await fetch(`${API}/admin/games`, { headers: authHeaders });
      if (gamesRes.ok) {
        const gamesData = await gamesRes.json();
        setGames(Array.isArray(gamesData.games || gamesData) ? (gamesData.games || gamesData) : []);
      }
    } catch (error) {
      console.error('Error loading games:', error);
      setGames([]);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Load questions and levels when a game is selected
  useEffect(() => {
    if (selectedGameForAdmin) {
      loadGameQuestions();
      loadGameLevels();
    }
  }, [selectedGameForAdmin, selectedLevelFilter]);

  const loadGameLevels = async () => {
    if (!selectedGameForAdmin) return;

    try {
      const response = await fetch(`${API}/games/${selectedGameForAdmin.id}/levels`);
      const data = await response.json();
      setGameLevels(data.levels || []);
    } catch (error) {
      console.error('Error loading levels:', error);
    }
  };

  const handleToggleGameVisibility = async (game) => {
    try {
      let response;
      const newIsActive = !game.is_active;

      // Handle Souk Bargain Game (Food) - uses its own settings endpoint
      if (game.is_souk_game) {
        response = await fetch(`${API}/admin/souk/game-settings`, {
          method: 'PUT',
          headers: authHeaders,
          body: JSON.stringify({ is_active: newIsActive, is_pro_only: game.is_premium || false })
        });
      }
      // Handle Souk Clothes Game - uses its own settings endpoint
      else if (game.is_clothes_game) {
        response = await fetch(`${API}/admin/souk-clothes/settings`, {
          method: 'PUT',
          headers: authHeaders,
          body: JSON.stringify({ is_active: newIsActive, is_pro_only: game.is_premium || false })
        });
      }
      // Handle Morocco Journey - uses game settings endpoint
      else if (game.is_morocco_journey) {
        response = await fetch(`${API}/game/toggle-visibility?is_visible=${newIsActive}`, {
          method: 'POST',
          headers: authHeaders
        });
      }
      // Default: use generic games endpoint
      else {
        response = await fetch(`${API}/admin/games/${game.id}`, {
          method: 'PUT',
          headers: authHeaders,
          body: JSON.stringify({ is_active: newIsActive })
        });
      }

      if (response.ok) {
        showNotification(
          game.is_active ? 'Game hidden from users' : 'Game now visible to users',
          'success'
        );
        setGames(prev => prev.map(g => g.id === game.id ? { ...g, is_active: newIsActive } : g));
      } else {
        showNotification('Failed to update game visibility', 'error');
      }
    } catch (error) {
      console.error('Error toggling game visibility:', error);
      showNotification('Failed to update game visibility', 'error');
    }
  };

  const handleToggleGamePremium = async (game) => {
    try {
      let response;
      const newIsPremium = !game.is_premium;

      // Handle Souk Bargain Game (Food) - uses its own settings endpoint
      if (game.is_souk_game) {
        response = await fetch(`${API}/admin/souk/game-settings`, {
          method: 'PUT',
          headers: authHeaders,
          body: JSON.stringify({ is_active: game.is_active !== false, is_pro_only: newIsPremium })
        });
      }
      // Handle Souk Clothes Game - uses its own settings endpoint
      else if (game.is_clothes_game) {
        response = await fetch(`${API}/admin/souk-clothes/settings`, {
          method: 'PUT',
          headers: authHeaders,
          body: JSON.stringify({ is_active: game.is_active !== false, is_pro_only: newIsPremium })
        });
      }
      // Handle Morocco Journey - uses game settings endpoint
      else if (game.is_morocco_journey) {
        response = await fetch(`${API}/game/toggle-pro?is_pro=${newIsPremium}`, {
          method: 'POST',
          headers: authHeaders
        });
      }
      // Default: use generic games endpoint
      else {
        response = await fetch(`${API}/admin/games/${game.id}`, {
          method: 'PUT',
          headers: authHeaders,
          body: JSON.stringify({ is_premium: newIsPremium })
        });
      }

      if (response.ok) {
        showNotification(
          game.is_premium ? 'Game now free for all users' : 'Game now requires Pro membership',
          'success'
        );
        setGames(prev => prev.map(g => g.id === game.id ? { ...g, is_premium: newIsPremium } : g));
      } else {
        showNotification('Failed to update game access', 'error');
      }
    } catch (error) {
      console.error('Error toggling game premium status:', error);
      showNotification('Failed to update game access', 'error');
    }
  };

  const handleToggleScriptOption = async (game, scriptType) => {
    try {
      const field = scriptType === 'arabic' ? 'allow_arabic_script' : 'allow_latin_script';
      const currentValue = game[field];

      // Prevent disabling both scripts
      if (currentValue) {
        const otherField = scriptType === 'arabic' ? 'allow_latin_script' : 'allow_arabic_script';
        if (!game[otherField]) {
          showNotification('At least one script option must be enabled', 'error');
          return;
        }
      }

      const response = await fetch(`${API}/admin/games/${game.id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ [field]: !currentValue })
      });

      if (response.ok) {
        showNotification(
          !currentValue
            ? `${scriptType === 'arabic' ? 'Arabic' : 'Latin'} script enabled`
            : `${scriptType === 'arabic' ? 'Arabic' : 'Latin'} script disabled`,
          'success'
        );
        loadData(); // Reload games list
      } else {
        showNotification('Failed to update script option', 'error');
      }
    } catch (error) {
      console.error('Error toggling script option:', error);
      showNotification('Failed to update script option', 'error');
    }
  };

  const openIconPicker = (game) => {
    setIconPickerGame(game);
  };

  const handleSelectIcon = async (url) => {
    if (!iconPickerGame) return;
    try {
      const res = await fetch(`${API}/admin/games/${iconPickerGame.id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ icon_url: url })
      });
      if (res.ok) {
        showNotification('Icon assigned to game', 'success');
        setIconPickerGame(null);
        loadData();
      } else {
        showNotification('Failed to assign icon', 'error');
      }
    } catch (err) {
      showNotification('Failed to assign icon', 'error');
    }
  };

  const handleRemoveIcon = async (game) => {
    try {
      const res = await fetch(`${API}/admin/games/${game.id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ icon_url: null })
      });
      if (res.ok) {
        showNotification('Reverted to emoji icon', 'success');
        loadData();
      }
    } catch (err) {
      showNotification('Failed to remove icon', 'error');
    }
  };

  const handleUpdateGameAudio = (game, newUrl) => {
    // Update local state immediately for better UX
    const updatedGames = games.map(g =>
      g.id === game.id ? { ...g, audio_url: newUrl } : g
    );
    setGames(updatedGames);
  };

  const saveGameAudio = async (game, audioUrl) => {
    try {
      const response = await fetch(`${API}/admin/games/${game.id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ audio_url: audioUrl || null })
      });

      if (response.ok) {
        showNotification('Background music URL updated successfully', 'success');
      } else {
        showNotification('Failed to update music URL', 'error');
      }
    } catch (error) {
      console.error('Error updating game audio:', error);
      showNotification('Failed to update music URL', 'error');
    }
  };

  const handleUpdateLevelTime = async (levelId, newTime) => {
    try {
      const response = await fetch(`${API}/admin/games/${selectedGameForAdmin.id}/levels/${levelId}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ time_per_question: newTime })
      });

      if (response.ok) {
        showNotification('Level time updated successfully!', 'success');
        loadGameLevels(); // Reload levels
      } else {
        showNotification('Failed to update level time', 'error');
      }
    } catch (error) {
      console.error('Error updating level time:', error);
      showNotification('Failed to update level time', 'error');
    }
  };

  const loadGameQuestions = async () => {
    if (!selectedGameForAdmin) return;

    try {
      const params = selectedLevelFilter ? `?level=${selectedLevelFilter}` : '';
      const response = await fetch(`${API}/admin/games/${selectedGameForAdmin.id}/questions${params}`, {
        headers: authHeaders
      });
      const data = await response.json();
      setGameQuestions(data.questions || []);
    } catch (error) {
      console.error('Error loading questions:', error);
    }
  };

  const handleSaveQuestion = async (e) => {
    e.preventDefault();

    const questionData = editingQuestion || newQuestion;

    try {
      if (editingQuestion) {
        // Update existing question
        const response = await fetch(`${API}/admin/games/${selectedGameForAdmin.id}/questions/${editingQuestion.id}`, {
          method: 'PUT',
          headers: authHeaders,
          body: JSON.stringify(questionData)
        });

        if (response.ok) {
          showNotification('Question updated successfully!', 'success');
          setShowAddQuestionModal(false);
          setEditingQuestion(null);
          loadGameQuestions();
        } else {
          const error = await response.json();
          showNotification(error.detail || 'Failed to update question', 'error');
        }
      } else {
        // Create new question
        const response = await fetch(`${API}/admin/games/${selectedGameForAdmin.id}/questions`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify(questionData)
        });

        if (response.ok) {
          showNotification('Question added successfully!', 'success');
          setShowAddQuestionModal(false);
          setNewQuestion({ level: 1, english_hint: '', darija_complete: '', missing_word: '', is_active: true });
          loadGameQuestions();
        } else {
          const error = await response.json();
          showNotification(error.detail || 'Failed to add question', 'error');
        }
      }
    } catch (error) {
      console.error('Error saving question:', error);
      showNotification('Failed to save question', 'error');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      const response = await fetch(`${API}/admin/games/${selectedGameForAdmin.id}/questions/${questionId}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (response.ok) {
        showNotification('Question deleted successfully!', 'success');
        loadGameQuestions();
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to delete question', 'error');
      }
    } catch (error) {
      console.error('Error deleting question:', error);
      showNotification('Failed to delete question', 'error');
    }
  };

  return (
    <div className="space-y-6">
                  <h3 className={`text-2xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    🎮 Games Management
                  </h3>

                  {/* Games Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {games.filter(game => game.name !== 'Learning Clock').map((game) => (
                      <div
                        key={game.id}
                        className={`rounded-2xl p-6 transition-all duration-300 ${
                          isDarkMode
                            ? 'bg-gray-800 shadow-xl hover:shadow-2xl border border-gray-700'
                            : 'bg-white shadow-lg hover:shadow-2xl border border-gray-200'
                        }`}
                      >
                        {/* Game Header */}
                        <div className="flex items-start gap-4 mb-6">
                          <div className="flex flex-col items-center gap-1">
                            {game.icon_url ? (
                              <img
                                src={`${API.replace('/api', '')}${game.icon_url}`}
                                alt={game.name}
                                className="w-14 h-14 object-contain"
                              />
                            ) : (
                              <div className="text-5xl">{game.icon}</div>
                            )}
                            <button
                              onClick={() => openIconPicker(game)}
                              className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-blue-400 hover:bg-gray-600' : 'bg-gray-100 text-blue-600 hover:bg-gray-200'}`}
                            >
                              Change
                            </button>
                          </div>
                          <div className="flex-1">
                            <h5 className={`text-xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                              {game.name}
                            </h5>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {game.description}
                            </p>
                          </div>
                        </div>

                        {/* Control Toggles */}
                        <div className="space-y-3 mb-6">
                          {/* Visibility Toggle */}
                          <div className={`flex items-center justify-between p-3 rounded-lg ${
                            isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <div className="flex items-center gap-2">
                              <span>👁️</span>
                              <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                Visibility
                              </span>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleGameVisibility(game);
                              }}
                              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all shadow-md ${
                                game.is_active
                                  ? 'bg-green-500 hover:bg-green-600 text-white'
                                  : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                            >
                              {game.is_active ? 'VISIBLE' : 'HIDDEN'}
                            </button>
                          </div>

                          {/* Premium Toggle */}
                          <div className={`flex items-center justify-between p-3 rounded-lg ${
                            isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <div className="flex items-center gap-2">
                              <span>⭐</span>
                              <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                Access
                              </span>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleGamePremium(game);
                              }}
                              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all shadow-md ${
                                game.is_premium
                                  ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white'
                                  : 'bg-blue-500 hover:bg-blue-600 text-white'
                              }`}
                            >
                              {game.is_premium ? '🔒 PRO ONLY' : '🔓 FREE'}
                            </button>
                          </div>

                          {/* Script Availability (DB games only) */}
                          {!game._isSpecial && <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                            <div className="flex items-center gap-2 mb-3">
                              <span>🔤</span>
                              <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                Script Options
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleScriptOption(game, 'arabic');
                                }}
                                className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all shadow-md ${
                                  game.allow_arabic_script
                                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                    : 'bg-gray-500 hover:bg-gray-600 text-white'
                                }`}
                              >
                                {game.allow_arabic_script ? '✓ Arabic' : '✗ Arabic'}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleScriptOption(game, 'latin');
                                }}
                                className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all shadow-md ${
                                  game.allow_latin_script
                                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                    : 'bg-gray-500 hover:bg-gray-600 text-white'
                                }`}
                              >
                                {game.allow_latin_script ? '✓ Latin' : '✗ Latin'}
                              </button>
                            </div>
                          </div>}
                        </div>

                        {/* Background Music URL (DB games only) */}
                        {!game._isSpecial && <>
                        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-2 mb-3">
                            <span>🎵</span>
                            <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                              Background Music
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="Paste Dropbox link here (see instructions below)"
                              value={game.audio_url || ''}
                              onChange={(e) => handleUpdateGameAudio(game, e.target.value)}
                              className={`flex-1 px-3 py-2 rounded-lg text-sm ${
                                isDarkMode
                                  ? 'bg-gray-600 text-white border border-gray-500 focus:border-blue-500'
                                  : 'bg-white border border-gray-300 focus:border-blue-500'
                              } focus:outline-none`}
                            />
                            <button
                              onClick={() => saveGameAudio(game, game.audio_url || '')}
                              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-sm shadow-md hover:shadow-lg transition-all"
                            >
                              💾 Save
                            </button>
                          </div>

                          {/* Dropbox Instructions */}
                          <div className={`mt-3 p-3 rounded-lg border ${isDarkMode ? 'bg-blue-900 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
                            <p className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                              📖 How to get Dropbox direct link:
                            </p>
                            <ol className={`text-xs space-y-1 ${isDarkMode ? 'text-blue-200' : 'text-blue-700'}`}>
                              <li>1. Upload your audio file to Dropbox</li>
                              <li>2. Click "Share" → "Create link"</li>
                              <li>3. Copy the link</li>
                              <li>4. <strong>Replace "dl=0" with "dl=1"</strong> at the end</li>
                              <li>5. Paste here and click Save</li>
                            </ol>
                            <p className={`text-xs mt-2 italic ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                              Example: ...dropbox.com/s/abc123/music.mp3?dl=1
                            </p>
                          </div>
                        </div>

                        {/* Manage Questions Button */}
                        <button
                          onClick={() => setSelectedGameForAdmin(game)}
                          className={`w-full py-3 rounded-lg font-bold transition-all shadow-md hover:shadow-lg ${
                            isDarkMode
                              ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                              : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                          } text-white`}
                        >
                          ⚙️ Manage Questions & Levels
                        </button>
                        </>}
                      </div>
                    ))}
                  </div>

                  {/* Questions & Levels Editor Modal */}
                  {selectedGameForAdmin && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                      <div className={`w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl ${
                        isDarkMode ? 'bg-gray-800' : 'bg-white'
                      } shadow-2xl`}>
                        {/* Header */}
                        <div className={`sticky top-0 p-6 border-b z-10 ${
                          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}>
                          <div className="flex items-center justify-between">
                            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                              ⚙️ {selectedGameForAdmin.name} - Questions & Levels
                            </h3>
                            <button
                              onClick={() => {
                                setSelectedGameForAdmin(null);
                                setEditingQuestion(null);
                                setSelectedScriptTab('arabic');
                              }}
                              className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-700 text-white' : 'hover:bg-gray-200'}`}
                            >
                              ✕
                            </button>
                          </div>

                          {/* Script Tabs */}
                          <div className="flex gap-2 mt-4">
                            <button
                              onClick={() => setSelectedScriptTab('arabic')}
                              className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                                selectedScriptTab === 'arabic'
                                  ? 'bg-purple-500 text-white'
                                  : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              🇲🇦 Arabic Script (الدارجة)
                            </button>
                            <button
                              onClick={() => setSelectedScriptTab('latin')}
                              className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                                selectedScriptTab === 'latin'
                                  ? 'bg-blue-500 text-white'
                                  : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              🔤 Latin Script (Darija)
                            </button>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-6">
                          {/* Filters Row */}
                          <div className="flex gap-4 flex-wrap">
                            <div className="flex-1 min-w-[200px]">
                              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                Filter by Level
                              </label>
                              <select
                                value={selectedLevelFilter || ''}
                                onChange={(e) => setSelectedLevelFilter(e.target.value || null)}
                                className={`w-full p-3 rounded-lg border ${
                                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                                }`}
                              >
                                <option value="">All Levels</option>
                                {[1,2,3,4,5,6,7,8,9,10].map(lvl => (
                                  <option key={lvl} value={lvl}>Level {lvl}</option>
                                ))}
                              </select>
                            </div>
                            <div className="flex items-end">
                              <button
                                onClick={() => setEditingQuestion({ isNew: true, use_arabic_script: selectedScriptTab === 'arabic' })}
                                className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium"
                              >
                                + Add Question ({selectedScriptTab === 'arabic' ? 'Arabic' : 'Latin'})
                              </button>
                            </div>
                          </div>

                          {/* Questions List */}
                          <div>
                            <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                              📝 {selectedScriptTab === 'arabic' ? 'Arabic' : 'Latin'} Questions
                              ({gameQuestions.filter(q =>
                                (selectedScriptTab === 'arabic' && q.use_arabic_script) ||
                                (selectedScriptTab === 'latin' && !q.use_arabic_script)
                              ).filter(q => !selectedLevelFilter || q.level === parseInt(selectedLevelFilter)).length})
                            </h4>

                            {gameQuestions.filter(q =>
                              ((selectedScriptTab === 'arabic' && q.use_arabic_script) ||
                               (selectedScriptTab === 'latin' && !q.use_arabic_script)) &&
                              (!selectedLevelFilter || q.level === parseInt(selectedLevelFilter))
                            ).length === 0 ? (
                              <div className={`text-center py-8 rounded-xl border-2 border-dashed ${
                                isDarkMode ? 'border-gray-600 text-gray-400' : 'border-gray-300 text-gray-500'
                              }`}>
                                No {selectedScriptTab} questions found. Add some!
                              </div>
                            ) : (
                              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                                {gameQuestions.filter(q =>
                                  ((selectedScriptTab === 'arabic' && q.use_arabic_script) ||
                                   (selectedScriptTab === 'latin' && !q.use_arabic_script)) &&
                                  (!selectedLevelFilter || q.level === parseInt(selectedLevelFilter))
                                ).map((q, idx) => (
                                  <div
                                    key={q.id || idx}
                                    className={`p-4 rounded-xl border ${
                                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between gap-4">
                                      <div className="flex-1">
                                        <div className={`text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                          Level {q.level} | {q.use_arabic_script ? '🇲🇦 Arabic' : '🔤 Latin'}
                                        </div>
                                        <div className={`font-medium text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'} ${q.use_arabic_script ? 'font-arabic text-right' : ''}`}>
                                          {q.display_sentence || q.darija_complete || '-'}
                                        </div>
                                        <div className={`text-sm mt-1 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                          Answer: <span className="font-bold">{q.missing_word || '-'}</span>
                                        </div>
                                        <div className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                          Hint: {q.english_hint || '-'}
                                        </div>
                                      </div>
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() => setEditingQuestion(q)}
                                          className="px-3 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                                        >
                                          ✏️ Edit
                                        </button>
                                        <button
                                          onClick={async () => {
                                            if (window.confirm('Delete this question?')) {
                                              try {
                                                const response = await fetch(`${API}/admin/games/${selectedGameForAdmin.id}/questions/${q.id}`, {
                                                  method: 'DELETE',
                                                  headers: authHeaders
                                                });
                                                if (response.ok) {
                                                  showNotification('Question deleted', 'success');
                                                  loadGameQuestions();
                                                }
                                              } catch (err) {
                                                showNotification('Delete failed', 'error');
                                              }
                                            }
                                          }}
                                          className="px-3 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600"
                                        >
                                          🗑️
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Levels Overview */}
                          <div>
                            <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                              📊 Levels Overview
                            </h4>
                            <div className="grid grid-cols-5 gap-2">
                              {[1,2,3,4,5,6,7,8,9,10].map(lvl => {
                                const arabicCount = gameQuestions.filter(q => q.level === lvl && q.use_arabic_script).length;
                                const latinCount = gameQuestions.filter(q => q.level === lvl && !q.use_arabic_script).length;
                                return (
                                  <div
                                    key={lvl}
                                    className={`p-3 rounded-lg text-center ${
                                      isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                                    }`}
                                  >
                                    <div className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                      Lvl {lvl}
                                    </div>
                                    <div className="text-xs text-purple-500">🇲🇦 {arabicCount}</div>
                                    <div className="text-xs text-blue-500">🔤 {latinCount}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Edit Question Modal - Complete version with script toggle */}
                  {editingQuestion && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                      <div className={`w-full max-w-lg rounded-2xl ${
                        isDarkMode ? 'bg-gray-800' : 'bg-white'
                      } shadow-2xl p-6`}>
                        <h4 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {editingQuestion.isNew ? '➕ Add Question' : '✏️ Edit Question'}
                          <span className={`ml-2 text-sm font-normal ${editingQuestion.use_arabic_script ? 'text-purple-400' : 'text-blue-400'}`}>
                            ({editingQuestion.use_arabic_script ? '🇲🇦 Arabic' : '🔤 Latin'})
                          </span>
                        </h4>

                        <div className="space-y-4">
                          {/* Script Toggle */}
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Script Type
                            </label>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setEditingQuestion({...editingQuestion, use_arabic_script: true})}
                                className={`flex-1 py-2 rounded-lg font-medium ${
                                  editingQuestion.use_arabic_script
                                    ? 'bg-purple-500 text-white'
                                    : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                🇲🇦 Arabic
                              </button>
                              <button
                                onClick={() => setEditingQuestion({...editingQuestion, use_arabic_script: false})}
                                className={`flex-1 py-2 rounded-lg font-medium ${
                                  !editingQuestion.use_arabic_script
                                    ? 'bg-blue-500 text-white'
                                    : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                🔤 Latin
                              </button>
                            </div>
                          </div>

                          {/* Level */}
                          <div>
                            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Level
                            </label>
                            <select
                              value={editingQuestion.level || 1}
                              onChange={(e) => setEditingQuestion({...editingQuestion, level: parseInt(e.target.value)})}
                              className={`w-full p-3 rounded-lg border ${
                                isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                              }`}
                            >
                              {[1,2,3,4,5,6,7,8,9,10].map(lvl => (
                                <option key={lvl} value={lvl}>Level {lvl}</option>
                              ))}
                            </select>
                          </div>

                          {/* Full Sentence */}
                          <div>
                            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Full Sentence (Darija)
                            </label>
                            <input
                              type="text"
                              value={editingQuestion.darija_complete || ''}
                              onChange={(e) => setEditingQuestion({...editingQuestion, darija_complete: e.target.value})}
                              className={`w-full p-3 rounded-lg border ${
                                isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                              } ${editingQuestion.use_arabic_script ? 'text-right font-arabic' : ''}`}
                              placeholder={editingQuestion.use_arabic_script ? 'الجملة كاملة بالدارجة' : 'Full sentence in Darija'}
                              dir={editingQuestion.use_arabic_script ? 'rtl' : 'ltr'}
                            />
                          </div>

                          {/* Missing Word (Answer) */}
                          <div>
                            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Missing Word (Answer)
                            </label>
                            <input
                              type="text"
                              value={editingQuestion.missing_word || ''}
                              onChange={(e) => setEditingQuestion({...editingQuestion, missing_word: e.target.value})}
                              className={`w-full p-3 rounded-lg border ${
                                isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                              } ${editingQuestion.use_arabic_script ? 'text-right font-arabic' : ''}`}
                              placeholder={editingQuestion.use_arabic_script ? 'الكلمة الناقصة' : 'The word to guess'}
                              dir={editingQuestion.use_arabic_script ? 'rtl' : 'ltr'}
                            />
                          </div>

                          {/* English Hint */}
                          <div>
                            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              English Hint
                            </label>
                            <input
                              type="text"
                              value={editingQuestion.english_hint || ''}
                              onChange={(e) => setEditingQuestion({...editingQuestion, english_hint: e.target.value})}
                              className={`w-full p-3 rounded-lg border ${
                                isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                              }`}
                              placeholder="English translation or hint"
                            />
                          </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                          <button
                            onClick={() => setEditingQuestion(null)}
                            className={`flex-1 py-3 rounded-lg font-medium ${
                              isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300'
                            }`}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                const url = editingQuestion.isNew
                                  ? `${API}/admin/games/${selectedGameForAdmin.id}/questions`
                                  : `${API}/admin/games/${selectedGameForAdmin.id}/questions/${editingQuestion.id}`;
                                const method = editingQuestion.isNew ? 'POST' : 'PUT';

                                const response = await fetch(url, {
                                  method,
                                  headers: authHeaders,
                                  body: JSON.stringify({
                                    level: editingQuestion.level || 1,
                                    darija_complete: editingQuestion.darija_complete,
                                    missing_word: editingQuestion.missing_word,
                                    english_hint: editingQuestion.english_hint,
                                    use_arabic_script: editingQuestion.use_arabic_script || false,
                                    is_active: true
                                  })
                                });

                                if (response.ok) {
                                  showNotification(editingQuestion.isNew ? 'Question added!' : 'Question updated!', 'success');
                                  setEditingQuestion(null);
                                  loadGameQuestions();
                                } else {
                                  const err = await response.json();
                                  showNotification(err.detail || 'Failed to save', 'error');
                                }
                              } catch (err) {
                                showNotification('Error saving question', 'error');
                              }
                            }}
                            className={`flex-1 py-3 rounded-lg font-medium text-white ${
                              editingQuestion.use_arabic_script ? 'bg-purple-500 hover:bg-purple-600' : 'bg-blue-500 hover:bg-blue-600'
                            }`}
                          >
                            {editingQuestion.isNew ? 'Add Question' : 'Save Changes'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Icon Picker — uses shared IconPicker (browses ALL Image Manager folders) */}
                  {iconPickerGame && (
                    <>
                      <IconPicker
                        API={API}
                        adminToken={adminToken}
                        isDarkMode={isDarkMode}
                        onClose={() => setIconPickerGame(null)}
                        onPick={(url) => handleSelectIcon(url)}
                      />
                    </>
                  )}
    </div>
  );
};

export default GamesAdmin;
